import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import {
  type DeliveryInfo,
  initialRecipientData,
  initialDeliveryInfo
} from '@/types/FunctionalModal';
import {
  type IDeliveryEditState,
  type IDeliveryEditInitParams,
  type IDeliveryValidationErrors,
  type IPvzManagementState,
  type IAddressManagementState,
  type IFormCoordinationState,
  type DeliveryInfoUpdate,
  type PvzManagementUpdate,
  type AddressManagementUpdate,
  type FormCoordinationUpdate,
  type ValidationErrorsUpdate,
  type ICacheState,
  type ICachedPvzData,
  initialPvzManagementState,
  initialAddressManagementState,
  initialValidationErrors,
  initialCacheState
} from './types';
import {
  fetchPvzListWithCacheThunk,
  selectPvzThunk
} from './deliveryEditThunks';

const initialFormCoordinationState: IFormCoordinationState = {
  deliveryInfo: initialDeliveryInfo,
  recipientData: initialRecipientData,
  editedRecipientData: initialRecipientData,
  pendingUpdates: {},
  isUpdating: false
};

const initialState: IDeliveryEditState = {
  formCoordination: initialFormCoordinationState,
  pvzManagement: initialPvzManagementState,
  addressManagement: initialAddressManagementState,
  validationErrors: initialValidationErrors,
  cache: initialCacheState,
  isLoading: false,
  error: null,
  settings: {
    isCustomerRedactor: false,
    recipientAddress: '',
    recipientCity: '',
    classNameForInput: '',
    assessedCost: undefined
  }
};

const deliveryEditSlice = createSlice({
  name: 'deliveryEdit',
  initialState,
  reducers: {
    initializeDeliveryEdit: (
      state,
      action: PayloadAction<IDeliveryEditInitParams>
    ) => {
      const {
        deliveryInfo,
        isCustomerRedactor = false,
        recipientAddress = '',
        recipientCity = '',
        classNameForInput = '',
        assessedCost,
        validationErrors
      } = action.payload;

      state.formCoordination.deliveryInfo = deliveryInfo;
      state.formCoordination.recipientData = initialRecipientData;
      state.formCoordination.editedRecipientData = initialRecipientData;

      state.settings = {
        isCustomerRedactor,
        recipientAddress,
        recipientCity,
        classNameForInput,
        assessedCost
      };

      if (deliveryInfo.type === 'delivery') {
        state.addressManagement.addressEdit = recipientAddress;
      } else {
        state.addressManagement.addressEdit = '';
      }

      if (validationErrors !== null && validationErrors !== undefined) {
        state.validationErrors = {
          ...state.validationErrors,
          ...validationErrors
        };
      }
    },

    updateDeliveryInfo: (state, action: PayloadAction<DeliveryInfoUpdate>) => {
      const updates = action.payload;
      state.formCoordination.deliveryInfo = {
        ...state.formCoordination.deliveryInfo,
        ...updates
      };

      if (updates.type !== undefined) {
        state.formCoordination.deliveryInfo.pvz_id = null;
        state.pvzManagement.selectedPvzId = null;
      }

      if (updates.provider !== undefined) {
        state.formCoordination.deliveryInfo.pvz_id = null;
        state.pvzManagement.selectedPvzId = null;
      }
    },

    updatePvzManagement: (
      state,
      action: PayloadAction<PvzManagementUpdate>
    ) => {
      state.pvzManagement = { ...state.pvzManagement, ...action.payload };
    },

    setPvzSearch: (state, action: PayloadAction<string>) => {
      state.pvzManagement.pvzSearch = action.payload;
    },

    setShowPvzSuggestions: (state, action: PayloadAction<boolean>) => {
      state.pvzManagement.showPvzSuggestions = action.payload;
    },

    clearPvzSelection: (state) => {
      state.pvzManagement.pvzSearch = '';
      state.pvzManagement.selectedPvzId = null;
      state.pvzManagement.showPvzSuggestions = true;
      state.pvzManagement.pvzList = [];
      state.pvzManagement.pvzError = null;
      state.formCoordination.deliveryInfo.pvz = '';
      state.formCoordination.deliveryInfo.pvz_id = null;
    },

    updateAddressManagement: (
      state,
      action: PayloadAction<AddressManagementUpdate>
    ) => {
      state.addressManagement = {
        ...state.addressManagement,
        ...action.payload
      };
    },

    setAddressEdit: (state, action: PayloadAction<string>) => {
      state.addressManagement.addressEdit = action.payload;
      if (state.formCoordination.deliveryInfo.type === 'delivery') {
        state.formCoordination.deliveryInfo.pvz = action.payload;
      }
    },

    updateFormCoordination: (
      state,
      action: PayloadAction<FormCoordinationUpdate>
    ) => {
      state.formCoordination = { ...state.formCoordination, ...action.payload };
    },

    updateValidationErrors: (
      state,
      action: PayloadAction<ValidationErrorsUpdate>
    ) => {
      state.validationErrors = { ...state.validationErrors, ...action.payload };
    },

    clearValidationErrors: (state) => {
      state.validationErrors = initialValidationErrors;
    },

    clearError: (state) => {
      state.error = null;
    },

    resetDeliveryEdit: (state) => {
      return { ...initialState, settings: state.settings };
    },

    clearDeliveryEdit: () => {
      return initialState;
    },

    // Кеширование ПВЗ
    savePvzToCache: (
      state,
      action: PayloadAction<{ key: string; data: ICachedPvzData }>
    ) => {
      const { key, data } = action.payload;
      state.cache.pvzCache[key] = data;
      state.cache.currentCacheKey = key;
    },

    loadPvzFromCache: (state, action: PayloadAction<string>) => {
      const cacheKey = action.payload;
      const cachedData = state.cache.pvzCache[cacheKey];
      if (cachedData !== null && cachedData !== undefined) {
        state.pvzManagement.pvzList = cachedData.pvzList;
        state.cache.currentCacheKey = cacheKey;
      }
    },

    clearCache: (state) => {
      state.cache = initialCacheState;
    },

    clearCacheForCity: (state, action: PayloadAction<string>) => {
      const city = action.payload;
      // Очищаем кеш для конкретного города
      const newCache: Record<string, ICachedPvzData> = {};
      Object.keys(state.cache.pvzCache).forEach((key) => {
        if (!key.startsWith(`${city}_`)) {
          newCache[key] = state.cache.pvzCache[key];
        }
      });
      state.cache.pvzCache = newCache;
      state.cache.currentCacheKey = null;
    }
  },

  extraReducers: (builder) => {
    // Кешированная загрузка ПВЗ
    builder
      .addCase(fetchPvzListWithCacheThunk.pending, (state) => {
        state.pvzManagement.isLoadingPvz = true;
        state.pvzManagement.pvzError = null;
      })
      .addCase(fetchPvzListWithCacheThunk.fulfilled, (state, action) => {
        state.pvzManagement.isLoadingPvz = false;
        state.pvzManagement.pvzList = action.payload.pvzList;
        state.pvzManagement.pvzError = null;

        // Сохраняем в кеш
        const { city, provider, type, pvzList } = action.payload;
        const cacheKey = `${city}_${provider}_${type}`;
        const cachedData: ICachedPvzData = {
          pvzList,
          timestamp: Date.now(),
          city,
          provider,
          type
        };
        state.cache.pvzCache[cacheKey] = cachedData;
        state.cache.currentCacheKey = cacheKey;
      })
      .addCase(fetchPvzListWithCacheThunk.rejected, (state, action) => {
        state.pvzManagement.isLoadingPvz = false;
        state.pvzManagement.pvzError =
          action.payload?.message ?? 'Ошибка загрузки ПВЗ';
      });

    // Выбор ПВЗ
    builder.addCase(selectPvzThunk.fulfilled, (state, action) => {
      const point = action.payload;
      const safeId =
        point.id != null && !isNaN(Number(point.id)) ? Number(point.id) : null;
      const trimmedAddress = point.address?.trim() ?? '';
      const safeAddress = trimmedAddress !== '' ? trimmedAddress : '';

      state.pvzManagement.selectedPvzId = safeId;
      state.pvzManagement.pvzSearch = safeAddress;
      state.pvzManagement.showPvzSuggestions = false;

      // Обновляем deliveryInfo
      state.formCoordination.deliveryInfo.pvz = safeAddress;
      state.formCoordination.deliveryInfo.pvz_id = safeId;

      // Обновляем данные получателя
      state.formCoordination.recipientData.pvz_id = safeId;
      state.formCoordination.recipientData.address = safeAddress;
      state.formCoordination.editedRecipientData.pvz_id = safeId;
      state.formCoordination.editedRecipientData.address = safeAddress;
    });
  }
});

export const {
  initializeDeliveryEdit,
  updateDeliveryInfo,
  updatePvzManagement,
  setPvzSearch,
  setShowPvzSuggestions,
  clearPvzSelection,
  updateAddressManagement,
  setAddressEdit,
  updateFormCoordination,
  updateValidationErrors,
  clearValidationErrors,
  clearError,
  resetDeliveryEdit,
  clearDeliveryEdit,
  savePvzToCache,
  loadPvzFromCache,
  clearCache,
  clearCacheForCity
} = deliveryEditSlice.actions;

export const selectDeliveryEditState = (state: RootState): IDeliveryEditState =>
  state.deliveryEdit;
export const selectDeliveryInfo = (state: RootState): DeliveryInfo =>
  state.deliveryEdit.formCoordination.deliveryInfo;
export const selectPvzManagement = (state: RootState): IPvzManagementState =>
  state.deliveryEdit.pvzManagement;
export const selectAddressManagement = (
  state: RootState
): IAddressManagementState => state.deliveryEdit.addressManagement;
export const selectValidationErrors = (
  state: RootState
): IDeliveryValidationErrors => state.deliveryEdit.validationErrors;
export const selectCache = (state: RootState): ICacheState =>
  state.deliveryEdit.cache;

export default deliveryEditSlice.reducer;
